import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Paper,
  Grid,
  InputAdornment,
  Chip,
  Stack,
} from '@mui/material';

import OpacityIcon from '@mui/icons-material/Opacity';
import SettingsIcon from '@mui/icons-material/Settings';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import TuneIcon from '@mui/icons-material/Tune';
import PowerIcon from '@mui/icons-material/Power';
import MemoryIcon from '@mui/icons-material/Memory';
import BarChartIcon from '@mui/icons-material/BarChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { getHsp, getHspIbge } from './api';
import { cores } from '../styles/styleUtils';
import usePerformanceRatio from '../hooks/usePerformanceRatio';
import PerformanceRatioInfo from '../components/PerformanceRatioInfo';

// ─── Constantes fixas ────────────────────────────────────────────────────────
const REND_INV    = 0.9;    // rendimento do inversor de frequência
const REND_ACOP   = 0.6;    // rendimento de acoplamento (fixo)
const HORAS_DIA   = 8;      // horas de operação solar efetiva (aproximação Brasil central)
const DIAS_MES    = 30;     // dias fixos para cálculo mensal
const G           = 9.81;   // gravidade (m/s²)
const RHO         = 1000;   // densidade da água (kg/m³)

const MESES      = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const CHAVES_HSP = ['hspJan','hspFev','hspMar','hspAbr','hspMai','hspJun','hspJul','hspAgo','hspSet','hspOut','hspNov','hspDez'];

const cvParaKw = (cv) => cv * 0.7457;

/**
 * Rendimento da bomba por faixa de potência (CV).
 * Até 5 CV  → 0,40
 * 5–15 CV   → 0,52
 * Acima 15  → 0,62
 */
const rendBombaPorPotencia = (cv) => {
  if (cv <= 5)  return 0.40;
  if (cv <= 15) return 0.52;
  return 0.62;
};

/**
 * Calcula dimensionamento pelo método WEG (abordagem reversa).
 *
 * PotCC  = Pinv / (rendAcop × rendFV)         onde Pinv = PotKw / rendInv
 * Consumo= Pot(cv) × diasFunc × horasFunc      (kWh/mês — aproximação CV≈kW)
 * kWpNec = Consumo / (HSP_min × 30 × rendFV)
 *
 * Para cada mês (abordagem reversa WEG):
 *   E_disp  = PotKit(Wp) × HSP / 1,25          (Wh/dia)
 *   E_hidr  = E_disp × n_bomba                 (Wh/dia)
 *   Qd      = 3600 × E_hidr / (Hm × g × ρ)    (m³/dia)
 *   Q_hora  = Qd / 8                            (m³/h)
 *   Q_mensal= Qd × 30                           (m³/mês)
 *
 * PotKit usada nos cálculos mensais = potenciaKwpNecessaria × 1000 (Wp)
 */
function calcularDimensionamento(inputs, hsp, rendFV, tarifaEnergia, potKitWp) {
  const { potenciaMotorCV, alturaManometrica, horasFuncionamento, diasFuncionamento } = inputs;

  const potKw     = cvParaKw(potenciaMotorCV);
  const pinv      = potKw / REND_INV;
  const potCC     = pinv / (REND_ACOP * rendFV);
  const rendBomba = rendBombaPorPotencia(potenciaMotorCV);

  // Consumo usa CV como aproximação de kW
  const consumoMensal = potenciaMotorCV * diasFuncionamento * horasFuncionamento;

  const hspMin = Math.min(...CHAVES_HSP.map((k) => hsp[k] || 0));

  const kwpNecessario = hspMin > 0
    ? consumoMensal / (hspMin * DIAS_MES * rendFV)
    : 0;

  // Potência do kit em Wp para o cálculo de vazão
  // Se o kit já foi selecionado, usa a potência real; senão usa o kWp calculado
  const kitWp = potKitWp || (kwpNecessario * 1000);

  const meses = CHAVES_HSP.map((chave, i) => {
    const hspMes    = hsp[chave] || 0;

    // Abordagem reversa WEG
    const eDisp     = (kitWp * hspMes) / 1.25;            // Wh/dia
    const eHidr     = eDisp * rendBomba;                   // Wh/dia
    const qDiaria   = alturaManometrica > 0
      ? (3600 * eHidr) / (alturaManometrica * G * RHO)
      : 0;                                                  // m³/dia
    const qHoraria  = qDiaria / HORAS_DIA;                 // m³/h
    const qMensal   = qDiaria * DIAS_MES;                  // m³/mês
    const economiaMes = consumoMensal * (tarifaEnergia || 0);

    return {
      mes:          MESES[i],
      hsp:          Number(hspMes.toFixed(4)),
      vazaoDiaria:  Number(qDiaria.toFixed(2)),
      vazaoHoraria: Number(qHoraria.toFixed(2)),
      vazaoMensal:  Number(qMensal.toFixed(2)),
      energiaMes:   Number(consumoMensal.toFixed(2)),
      economiaMes:  Number(economiaMes.toFixed(2)),
    };
  });

  return {
    potCC:                 Number(potCC.toFixed(3)),
    potInversor:           Number(pinv.toFixed(3)),
    rendBomba,
    potenciaKwpNecessaria: Number(kwpNecessario.toFixed(3)),
    consumoMensal:         Number(consumoMensal.toFixed(2)),
    hspMin:                Number(hspMin.toFixed(2)),
    diasOperacao:          DIAS_MES,
    horasOperacao:         HORAS_DIA,
    rendFV,
    meses,
    hspCompleto: hsp,
  };
}

// ─── Componente ──────────────────────────────────────────────────────────────
const DimensionamentoBombeamento = ({ proposta, onDimensionado, onVoltar }) => {
  const estadoPR = usePerformanceRatio(proposta, 'bombeamento');
  const rendFV = estadoPR.pr;

  // Inicializa com dados existentes se a proposta já foi dimensionada antes
  const dadosExistentes = proposta?.dadosBombeamento || {};

  const [possuiBomba,        setPossuiBomba]        = useState(dadosExistentes.possuiBomba        || 'sim');
  const [potenciaMotorCV,    setPotenciaMotorCV]     = useState(dadosExistentes.potenciaMotorCV    || '');
  const [tensaoOperacao,     setTensaoOperacao]      = useState(dadosExistentes.tensaoOperacao     || '');
  const [vazaoAtual,         setVazaoAtual]          = useState(dadosExistentes.vazaoAtual         || '');
  const [horasFuncionamento, setHorasFuncionamento]  = useState(dadosExistentes.horasFuncionamento || '');
  const [alturaManometrica,  setAlturaManometrica]   = useState(dadosExistentes.alturaManometrica  || '');
  const [diasFuncionamento,  setDiasFuncionamento]   = useState(dadosExistentes.diasFuncionamento  || '');
  const [tarifaEnergia,      setTarifaEnergia]       = useState(dadosExistentes.tarifaEnergia      || '');

  const [hsp,             setHsp]             = useState(null);
  const [loadingHsp,      setLoadingHsp]      = useState(false);
  // Se já tem dimensionamento salvo, inicializa como calculado
  const [dimensionamento, setDimensionamento] = useState(proposta?.dimensionamentoBomb || null);
  const [calculado,       setCalculado]       = useState(!!proposta?.dimensionamentoBomb);
  const [erro,            setErro]            = useState('');

  // ── Busca HSP ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const ibge   = proposta?.localinstalacao?.cidade?.id;
    const cidade = proposta?.localinstalacao?.cidade?.nome;
    const uf     = proposta?.localinstalacao?.estado?.sigla;
    if (!ibge && !cidade) return;
    setLoadingHsp(true);
    setErro('');
    setHsp(null);
    const buscar = async () => {
      let res = null;
      if (ibge) {
        try { res = await getHspIbge(Number(ibge)); } catch(e) { /* fallback */ }
      }
      if (!res && cidade && uf) {
        try { res = await getHsp(cidade, uf); } catch(e) { /* não encontrado */ }
      }
      if (res) { setHsp(res); }
      else { setErro('HSP não encontrado para a cidade. Verifique o local de instalação.'); }
      setLoadingHsp(false);
    };
    buscar();
  }, [proposta?.localinstalacao?.cidade?.id, proposta?.localinstalacao?.cidade?.nome]);

  // ── Busca PR e Tarifa dos índices ─────────────────────────────────────────
  useEffect(() => {
    const indices = estadoPR.indices;
    if (Array.isArray(indices)) {
          const tarifa = indices.find((i) =>
            i.nome?.toLowerCase().includes('tarifa') || i.nome?.toLowerCase().includes('kwh')
          );
          if (tarifa?.valor) setTarifaEnergia(String(tarifa.valor));
    }
  }, [estadoPR.indices]);

  // ── Validação ─────────────────────────────────────────────────────────────
  const inputsValidos = () => {
    if (!hsp) return false;
    if (!potenciaMotorCV || !alturaManometrica || !horasFuncionamento || !diasFuncionamento) return false;
    if (Number(potenciaMotorCV) <= 0 || Number(alturaManometrica) <= 0) return false;
    if (Number(horasFuncionamento) <= 0 || Number(diasFuncionamento) <= 0) return false;
    return true;
  };

  // ── Calcular ──────────────────────────────────────────────────────────────
  const handleCalcular = () => {
    if (!estadoPR.pronto) { setErro(estadoPR.erro || 'Aguarde o carregamento do PR.'); return; }
    setErro('');
    setCalculado(false);
    setDimensionamento(null);
    if (!inputsValidos()) {
      setErro('Preencha todos os campos obrigatórios antes de calcular.');
      return;
    }
    const inputs = {
      potenciaMotorCV:    Number(potenciaMotorCV),
      alturaManometrica:  Number(alturaManometrica),
      horasFuncionamento: Number(horasFuncionamento),
      diasFuncionamento:  Number(diasFuncionamento),
    };
    const result = calcularDimensionamento(inputs, hsp, rendFV, Number(tarifaEnergia) || 0, null);
    setDimensionamento(result);
    setCalculado(true);
  };

  // ── Confirmar ─────────────────────────────────────────────────────────────
  const handleConfirmar = () => {
    if (!dimensionamento) return;
    onDimensionado?.({
      dimensionamento,
      inputs: {
        possuiBomba,
        potenciaMotorCV:    Number(potenciaMotorCV),
        tensaoOperacao:     Number(tensaoOperacao),
        vazaoAtual:         Number(vazaoAtual),
        horasFuncionamento: Number(horasFuncionamento),
        alturaManometrica:  Number(alturaManometrica),
        diasFuncionamento:  Number(diasFuncionamento),
        tarifaEnergia:      Number(tarifaEnergia) || 0,
      },
    });
  };

  const cidadeNome  = proposta?.localinstalacao?.cidade?.nome || '–';
  const estadoNome  = proposta?.localinstalacao?.estado?.sigla || '';
  const hspMinAtual = hsp ? Math.min(...CHAVES_HSP.map((k) => hsp[k] || 0)) : null;

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>

      <Stack direction="row" alignItems="center" gap={1} mb={1}>
        <OpacityIcon sx={{ color: cores.laranja }} />
        <Typography variant="h5" fontWeight={700} sx={{ color: cores.textoClaro }}>
          Dimensionar Sistema de Bombeamento
        </Typography>
      </Stack>
      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <Typography variant="body2" sx={{ color: cores.textoClaro, opacity: 0.75 }}>
          Local de instalação: <strong>{cidadeNome}{estadoNome ? ` / ${estadoNome}` : ''}</strong>
        </Typography>
        {loadingHsp && <CircularProgress size={12} />}
        {hspMinAtual !== null && (
          <Chip icon={<WbSunnyIcon />} label={`HSP mín: ${hspMinAtual.toFixed(2)} h/dia`} size="small" color="success" />
        )}
        <PerformanceRatioInfo estado={estadoPR} />
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <SettingsIcon fontSize="small" sx={{ color: cores.laranja }} />
          <Typography variant="subtitle1" fontWeight={600}>Motobomba</Typography>
        </Stack>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>O cliente já possui motobomba?</InputLabel>
          <Select value={possuiBomba} label="O cliente já possui motobomba?"
            onChange={(e) => setPossuiBomba(e.target.value)}>
            <MenuItem value="sim">Sim, já possui</MenuItem>
            <MenuItem value="nao" disabled>Não possui bomba (em breve)</MenuItem>
          </Select>
        </FormControl>

        {possuiBomba === 'sim' && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth size="small" label="Potência do Motor" type="number"
                value={potenciaMotorCV} onChange={(e) => setPotenciaMotorCV(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SettingsIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">CV</InputAdornment>,
                }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth size="small" label="Tensão de Operação" type="number"
                value={tensaoOperacao} onChange={(e) => setTensaoOperacao(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><FlashOnIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">V</InputAdornment>,
                }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth size="small" label="Vazão Atual" type="number"
                value={vazaoAtual} onChange={(e) => setVazaoAtual(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><OpacityIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">m³/h</InputAdornment>,
                }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth size="small" label="Altura Manométrica" type="number"
                value={alturaManometrica} onChange={(e) => setAlturaManometrica(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><ArrowUpwardIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth size="small" label="Horas de Funcionamento" type="number"
                value={horasFuncionamento} onChange={(e) => setHorasFuncionamento(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AccessTimeIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">h/dia</InputAdornment>,
                }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth size="small" label="Dias de Funcionamento" type="number"
                value={diasFuncionamento} onChange={(e) => setDiasFuncionamento(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><CalendarMonthIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">dias/mês</InputAdornment>,
                }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth size="small" label="Tarifa de Energia" type="number"
                value={tarifaEnergia} onChange={(e) => setTarifaEnergia(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">R$/kWh</InputAdornment>,
                }} />
            </Grid>
          </Grid>
        )}
      </Paper>

      <Box display="flex" gap={2} mb={3}>
        {onVoltar && (
          <Button
            variant="outlined"
            onClick={onVoltar}
            startIcon={<ArrowBackIcon />}
            sx={{ color: cores.textoClaro, borderColor: cores.textoClaro }}
          >
            Voltar
          </Button>
        )}
        <Button variant="contained" color="primary" onClick={handleCalcular}
          disabled={!hsp || loadingHsp || !estadoPR.pronto}>
          Calcular Dimensionamento
        </Button>
      </Box>

      {calculado && dimensionamento && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            <BarChartIcon sx={{ color: cores.laranja }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: cores.textoClaro }}>
              Resultado do Dimensionamento
            </Typography>
          </Stack>

          <Grid container spacing={2} mb={3}>
            {[
              { label: 'Potência CC',        value: `${dimensionamento.potCC} kW`, Icone: PowerIcon },
              { label: 'Potência Inversor',  value: `${dimensionamento.potInversor} kW`, Icone: MemoryIcon },
              { label: 'Rend. Bomba',        value: `${(dimensionamento.rendBomba * 100).toFixed(0)}%`, Icone: TuneIcon },
              { label: 'Consumo Mensal',     value: `${dimensionamento.consumoMensal} kWh/mês`, Icone: FlashOnIcon },
              { label: 'HSP Mínimo',         value: `${dimensionamento.hspMin} h/dia`, Icone: WbSunnyIcon },
              { label: 'Vazão Média Corrigida', value: `${dimensionamento.meses?.length > 0 ? (dimensionamento.meses.reduce((s, m) => s + (m.vazaoHoraria || 0), 0) / 12).toFixed(2) : '–'} m³/h`, Icone: OpacityIcon },
            ].map((kpi) => (
              <Grid item xs={6} md={4} key={kpi.label}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <kpi.Icone fontSize="small" sx={{ color: cores.laranja, mb: 0.25 }} />
                  <Typography variant="caption" color="text.secondary" display="block">{kpi.label}</Typography>
                  <Typography variant="subtitle2" fontWeight={700}>{kpi.value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Paper variant="outlined" sx={{ mb: 3, overflow: 'auto' }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ p: 1.5, pb: 0 }}>
              <CalendarMonthIcon fontSize="small" sx={{ color: cores.laranja }} />
              <Typography variant="subtitle2" fontWeight={600}>Detalhamento Mensal</Typography>
            </Stack>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  {['Mês','HSP (h/dia)','Vazão Diária (m³/dia)','Vazão Horária (m³/h)','Vazão Mensal (m³/mês)','Consumo (kWh/mês)','Economia (R$)'].map((h) => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dimensionamento.meses.map((m, i) => (
                  <tr key={m.mes} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0' }}>{m.mes}</td>
                    <td style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0' }}>{m.hsp}</td>
                    <td style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0' }}>{m.vazaoDiaria}</td>
                    <td style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0' }}>{m.vazaoHoraria ?? '–'}</td>
                    <td style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0' }}>{m.vazaoMensal ?? '–'}</td>
                    <td style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0' }}>{m.energiaMes ?? '–'}</td>
                    <td style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0' }}>
                      {m.economiaMes != null ? m.economiaMes.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>

          <Box display="flex" gap={2}>
            <Button variant="contained" color="success" onClick={handleConfirmar} endIcon={<CheckCircleIcon />}>
              Confirmar e Continuar
            </Button>
            <Button variant="outlined" sx={{ color: cores.textoClaro, borderColor: cores.textoClaro }}
              startIcon={<RefreshIcon />}
              onClick={() => { setCalculado(false); setDimensionamento(null); }}>
              Recalcular
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DimensionamentoBombeamento;
